/**
 * Form GSTR-3B, cell by cell (C-03, FR-E18): which rows the form has, in its order and its
 * words, and how one cell reads — a figure, a figure a person typed with a note, or a state
 * in words. Kept out of `Gstr3b.tsx` so the reading is tested without rendering the screen.
 *
 * **A state is never a number.** Every cell the service emits is stamped by the rule
 * `gstr3b.cell_source` as `computed`, `typed` or `stated` (`source.kind`); a stated cell
 * carries `state` and no figures, and the screen prints its words where the figure would be.
 */

export type Cell = Record<string, unknown> | undefined

export interface FormRow {
  id: string
  label: string
}

/** 3.1, the form's wording. */
export const OUTWARD_ROWS: FormRow[] = [
  { id: '3.1(a)', label: '(a) Outward taxable supplies (other than zero rated, nil rated and exempted)' },
  { id: '3.1(b)', label: '(b) Outward taxable supplies (zero rated)' },
  { id: '3.1(c)', label: '(c) Other outward supplies (nil rated, exempted)' },
  { id: '3.1(d)', label: '(d) Inward supplies (liable to reverse charge)' },
  { id: '3.1(e)', label: '(e) Non-GST outward supplies' },
]

/** 3.1.1, supplies notified under section 9(5). */
export const ECO_ROWS: FormRow[] = [
  {
    id: '3.1.1(i)',
    label: '(i) Taxable supplies on which electronic commerce operator pays tax u/s 9(5) [to be furnished by the electronic commerce operator]',
  },
  {
    id: '3.1.1(ii)',
    label: '(ii) Taxable supplies made by the registered person through electronic commerce operator, on which electronic commerce operator is required to pay tax u/s 9(5) [to be furnished by the registered person making supplies through electronic commerce operator]',
  },
]

/** 3.2, by recipient type. */
export const INTER_STATE_ROWS: FormRow[] = [
  { id: '3.2(unregistered)', label: 'Supplies made to unregistered persons' },
  { id: '3.2(composition)', label: 'Supplies made to composition taxable persons' },
  { id: '3.2(uin)', label: 'Supplies made to UIN holders' },
]

/** 4, eligible ITC, with the form's sub-rows. `indent` marks a sub-row. */
export const ITC_ROWS: Array<FormRow & { indent?: boolean }> = [
  { id: '4A', label: '(A) ITC Available (whether in full or part)' },
  { id: '4A(1)', label: '(1) Import of goods', indent: true },
  { id: '4A(2)', label: '(2) Import of services', indent: true },
  { id: '4A(3)', label: '(3) Inward supplies liable to reverse charge (other than 1 & 2 above)', indent: true },
  { id: '4A(4)', label: '(4) Inward supplies from ISD', indent: true },
  { id: '4A(5)', label: '(5) All other ITC', indent: true },
  { id: '4B', label: '(B) ITC Reversed' },
  { id: '4B(1)', label: '(1) As per rules 38, 42 & 43 of CGST Rules and section 17(5)', indent: true },
  { id: '4B(2)', label: '(2) Others', indent: true },
  { id: '4C', label: '(C) Net ITC Available (A) – (B)' },
  // The rows as the service computes them (FR-E18): blocked credit under section 17(5), and
  // other ineligible credit. The wording of 4(D) in the form in force is to be verified with
  // the practitioner, and each cell's sentence says so.
  { id: '4D(1)', label: '(D)(1) Ineligible ITC — as per section 17(5)' },
  { id: '4D(2)', label: '(D)(2) Ineligible ITC — others' },
]

/** Table 5 — inward supplies, inter- and intra-state. */
export const INWARD_EXEMPT_ROWS: FormRow[] = [
  { id: '5(exempt)', label: 'From a supplier under composition scheme, Exempt and Nil rated supply' },
  { id: '5(non_gst)', label: 'Non GST supply' },
]

/** 5.1 — interest and late fee, per head. */
export const INTEREST_ROWS: FormRow[] = [
  { id: '5.1(interest)', label: 'Interest' },
  { id: '5.1(late_fee)', label: 'Late fee' },
]

/** The cells a person types, with a note (V-d). The service's ids. */
export const TYPED_CELLS: FormRow[] = [
  { id: '4B(1)', label: '4(B)(1) Reversal under rules 38, 42 and 43 and section 17(5)' },
  { id: '4B(2)', label: '4(B)(2) Other reversals' },
  { id: '4D(1)', label: '4(D)(1) Ineligible credit — an addition' },
  { id: '4D(2)', label: '4(D)(2) Other ineligible credit — an addition' },
]

export const STATE_LABELS: Record<string, string> = {
  pending_verification: 'Awaiting verification',
  not_computed: 'Not computed',
  // V-d was answered on 2026-09-10 (Q2): this app does not compute the Rule 42/43 reversal.
  // "Not computed" read as an unfinished job; this says a person decides it.
  decided_outside: 'Decided outside ProGST',
  declined: 'Not claimed — matched 2B only',
  awaiting_2b: '2B not imported for this period',
  not_declared: 'No 2B has declared reverse charge',
  // C-03: what ProGST cannot know (a buyer's registration type, a register with no rate).
  not_captured: 'Not captured',
  // C-03: 3.1.1(i) is the operator's own line.
  not_applicable: 'Not applicable',
}

export type CellKind = 'computed' | 'typed' | 'stated'

/** A cell's state, when it has one instead of a figure. */
export function stateOf(cell: Cell): string | null {
  const s = cell?.state
  return typeof s === 'string' ? s : null
}

export function stateWords(state: string): string {
  return STATE_LABELS[state] ?? 'Not shown'
}

/** The service's sentence for a person (`source.says`). */
export function saysOf(cell: Cell): string | null {
  const src = cell?.source as Record<string, unknown> | undefined
  return typeof src?.says === 'string' ? src.says : null
}

/** computed / typed / stated, as `gstr3b.cell_source` stamped it; inferred for an older 3B. */
export function kindOf(cell: Cell): CellKind | null {
  if (!cell) return null
  const src = cell.source as Record<string, unknown> | undefined
  const kind = src?.kind
  if (kind === 'computed' || kind === 'typed' || kind === 'stated') return kind
  return stateOf(cell) ? 'stated' : 'computed'
}

/** The note a person gave with a typed figure, or null. */
export function typedNote(cell: Cell): string | null {
  const src = cell?.source as Record<string, unknown> | undefined
  const typed = src?.typed as Record<string, unknown> | undefined
  return typed && typeof typed.note === 'string' && typed.note.trim() ? typed.note : null
}

/** A cell's figure in one column, in paise, or null where the cell carries none. */
export function figureOf(cell: Cell, column: string): number | null {
  if (!cell || stateOf(cell)) return null
  const value = cell[column]
  return typeof value === 'number' ? value : null
}

/**
 * The words beside a late fee or interest figure: whether the reference figures behind it
 * are verified. A cell with `verified: false` was computed from placeholder parameters.
 * D-142: a late month's interest is reckoned to the day it was prepared and is to confirm;
 * its sentence says to what day, and this chip says it is not settled.
 */
export function verifiedWords(cell: Cell): string | null {
  if (!cell || stateOf(cell)) return null
  if (cell.verified === false) return 'Reference figures not yet verified'
  if (typeof cell.to_confirm === 'string' && cell.to_confirm) return 'To confirm'
  return null
}

/** The Rule 42/43 inputs ProGST lists beside 4(B)(1) — listed, never computed (V-d). */
export interface ListedInputs {
  exemptTurnover: number | null
  totalTurnover: number | null
  inputTax: Record<string, number> | null
  notCaptured: string | null
}

export function listedInputs(cell: Cell): ListedInputs | null {
  const src = cell?.source as Record<string, unknown> | undefined
  const listed = src?.inputs_listed as Record<string, unknown> | undefined
  if (!listed) return null
  const t = listed.total_input_tax_T as Record<string, unknown> | undefined
  const tax =
    t && !('state' in t)
      ? Object.fromEntries(
          Object.entries(t).filter(([, v]) => typeof v === 'number') as Array<[string, number]>,
        )
      : null
  return {
    exemptTurnover: typeof listed.exempt_turnover_E === 'number' ? listed.exempt_turnover_E : null,
    totalTurnover: typeof listed.total_turnover_F === 'number' ? listed.total_turnover_F : null,
    inputTax: tax,
    notCaptured: typeof listed.T1_T2_T3_T4_Tc === 'string' ? listed.T1_T2_T3_T4_Tc : null,
  }
}

/** Rupees typed by a person → whole paise, or null when it is not a non-negative amount. */
export function paiseFromRupees(text: string): number | null {
  const clean = text.replace(/,/g, '').trim()
  if (clean === '') return 0
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null
  const [whole, frac = ''] = clean.split('.')
  return Number(whole) * 100 + Number((frac + '00').slice(0, 2))
}
